import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { 
  ArrowLeft, ExternalLink, Copy, Check, RefreshCw, 
  Globe, Building2, Home, Settings, ChevronRight
} from 'lucide-react';
import { API } from '@/App';

export const PortalExportsPage = () => {
  const navigate = useNavigate();
  const token = localStorage.getItem('token');
  const [loading, setLoading] = useState(true);
  const [feedUrls, setFeedUrls] = useState(null);
  const [accounts, setAccounts] = useState(null);
  const [copiedUrl, setCopiedUrl] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [feedsRes, accountsRes] = await Promise.all([
        axios.get(`${API}/portal/feed-urls`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/portal/accounts`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      setFeedUrls(feedsRes.data.feeds);
      setAccounts(accountsRes.data);
    } catch (error) {
      console.error('Error fetching portal data:', error);
      toast.error('Failed to load portal configuration');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async (url, id) => {
    try {
      await navigator.clipboard.writeText(url);
      setCopiedUrl(id);
      toast.success('URL copied to clipboard');
      setTimeout(() => setCopiedUrl(null), 2000);
    } catch (error) {
      toast.error('Failed to copy URL');
    }
  };

  const openFeedPreview = (url) => {
    window.open(url, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <RefreshCw className="w-8 h-8 animate-spin text-slate-400" />
      </div>
    );
  }

  const portalConfig = [
    {
      id: 'rightmove',
      name: 'Rightmove',
      logo: '🏠',
      color: 'bg-green-500',
      accounts: [
        { id: '172790', name: 'Default (Kempston)', types: ['Sales', 'Lettings'] },
        { id: '240398', name: 'Cranfield', types: ['Sales'] }
      ],
      feeds: feedUrls?.rightmove
    },
    {
      id: 'zoopla',
      name: 'Zoopla',
      logo: '🔵',
      color: 'bg-purple-500',
      accounts: [
        { id: '85589', name: 'Realtime', types: ['Sales', 'Lettings'] },
        { id: '125311', name: 'Cranfield', types: ['Sales', 'Lettings'] }
      ],
      feeds: feedUrls?.zoopla
    },
    {
      id: 'onthemarket',
      name: 'OnTheMarket',
      logo: '🏡',
      color: 'bg-orange-500',
      accounts: [
        { id: '47406', name: 'Default', types: ['Sales', 'Lettings'] }
      ],
      feeds: feedUrls?.onthemarket
    }
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b sticky top-14 z-10">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate(-1)}>
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-slate-800">Portal Exports</h1>
              <p className="text-sm text-slate-500">Rightmove, Zoopla & OnTheMarket feeds</p>
            </div>
          </div>
          <Button variant="outline" onClick={fetchData}>
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto p-6 space-y-6">
        {/* Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
          <div className="flex gap-3">
            <Globe className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="font-medium text-blue-800">How Portal Feeds Work</h3>
              <p className="text-sm text-blue-700 mt-1">
                Property portals (Rightmove, Zoopla, OnTheMarket) pull your listings automatically from these feed URLs. 
                Copy the relevant URL and provide it to the portal when configuring your data feed.
              </p>
            </div>
          </div>
        </div>

        {/* Portal Cards */}
        <div className="grid gap-6">
          {portalConfig.map((portal) => (
            <div key={portal.id} className="bg-white rounded-xl shadow-sm overflow-hidden">
              {/* Portal Header */}
              <div className={`${portal.color} px-6 py-4 flex items-center justify-between`}>
                <div className="flex items-center gap-3">
                  <span className="text-2xl">{portal.logo}</span>
                  <h2 className="text-xl font-bold text-white">{portal.name}</h2>
                </div>
                <div className="flex items-center gap-2">
                  {portal.accounts.map((acc) => (
                    <span key={acc.id} className="bg-white/20 text-white text-xs px-2 py-1 rounded">
                      {acc.name} ({acc.id})
                    </span>
                  ))}
                </div>
              </div>

              {/* Feed URLs */}
              <div className="p-6">
                <div className="grid md:grid-cols-2 gap-4">
                  {/* Sales Feed */}
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Building2 className="w-4 h-4 text-emerald-500" />
                      <span className="font-medium text-slate-700">Sales Feed</span>
                    </div>
                    {portal.feeds?.sales && (
                      <div className="space-y-2">
                        {typeof portal.feeds.sales === 'string' ? (
                          <FeedUrlRow 
                            label="XML"
                            url={portal.feeds.sales}
                            id={`${portal.id}-sales-xml`}
                            copiedUrl={copiedUrl}
                            onCopy={copyToClipboard}
                            onPreview={openFeedPreview}
                          />
                        ) : (
                          <>
                            <FeedUrlRow 
                              label="XML"
                              url={portal.feeds.sales.xml}
                              id={`${portal.id}-sales-xml`}
                              copiedUrl={copiedUrl}
                              onCopy={copyToClipboard}
                              onPreview={openFeedPreview}
                            />
                            {portal.feeds.sales.blm && (
                              <FeedUrlRow 
                                label="BLM"
                                url={portal.feeds.sales.blm}
                                id={`${portal.id}-sales-blm`}
                                copiedUrl={copiedUrl}
                                onCopy={copyToClipboard}
                                onPreview={openFeedPreview}
                              />
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>

                  {/* Lettings Feed */}
                  <div className="border rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <Home className="w-4 h-4 text-blue-500" />
                      <span className="font-medium text-slate-700">Lettings Feed</span>
                    </div>
                    {portal.feeds?.lettings && (
                      <div className="space-y-2">
                        {typeof portal.feeds.lettings === 'string' ? (
                          <FeedUrlRow 
                            label="XML"
                            url={portal.feeds.lettings}
                            id={`${portal.id}-lettings-xml`}
                            copiedUrl={copiedUrl}
                            onCopy={copyToClipboard}
                            onPreview={openFeedPreview}
                          />
                        ) : (
                          <>
                            <FeedUrlRow 
                              label="XML"
                              url={portal.feeds.lettings.xml}
                              id={`${portal.id}-lettings-xml`}
                              copiedUrl={copiedUrl}
                              onCopy={copyToClipboard}
                              onPreview={openFeedPreview}
                            />
                            {portal.feeds.lettings.blm && (
                              <FeedUrlRow 
                                label="BLM"
                                url={portal.feeds.lettings.blm}
                                id={`${portal.id}-lettings-blm`}
                                copiedUrl={copiedUrl}
                                onCopy={copyToClipboard}
                                onPreview={openFeedPreview}
                              />
                            )}
                          </>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Instructions */}
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h3 className="text-lg font-semibold text-slate-800 mb-4 flex items-center gap-2">
            <Settings className="w-5 h-5" />
            Setup Instructions
          </h3>
          <div className="space-y-4 text-sm text-slate-600">
            <div className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 font-medium">1</span>
              <div>
                <p className="font-medium text-slate-700">Copy the Feed URL</p>
                <p>Click the copy button next to the appropriate feed (Sales/Lettings, XML/BLM format)</p>
              </div>
            </div>
            <div className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 font-medium">2</span>
              <div>
                <p className="font-medium text-slate-700">Configure Your Portal Account</p>
                <p>Log in to your Rightmove/Zoopla/OnTheMarket account and navigate to Data Feed settings</p>
              </div>
            </div>
            <div className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 font-medium">3</span>
              <div>
                <p className="font-medium text-slate-700">Paste the Feed URL</p>
                <p>Enter the copied URL as your XML data feed source. The portal will automatically pull listings from this URL.</p>
              </div>
            </div>
            <div className="flex gap-3">
              <span className="flex-shrink-0 w-6 h-6 rounded-full bg-slate-100 flex items-center justify-center text-slate-600 font-medium">4</span>
              <div>
                <p className="font-medium text-slate-700">Test & Verify</p>
                <p>Use the preview button to check the feed content. Contact portal support if you encounter issues.</p>
              </div>
            </div>
          </div>
        </div>

        {/* Quick Stats */}
        <div className="grid grid-cols-3 gap-4">
          <div className="bg-white rounded-xl shadow-sm p-4 text-center">
            <p className="text-3xl font-bold text-emerald-600">--</p>
            <p className="text-sm text-slate-500">Sales Properties</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4 text-center">
            <p className="text-3xl font-bold text-blue-600">--</p>
            <p className="text-sm text-slate-500">Lettings Properties</p>
          </div>
          <div className="bg-white rounded-xl shadow-sm p-4 text-center">
            <p className="text-3xl font-bold text-purple-600">3</p>
            <p className="text-sm text-slate-500">Active Portals</p>
          </div>
        </div>
      </main>
    </div>
  );
};

// Helper component for feed URL rows
const FeedUrlRow = ({ label, url, id, copiedUrl, onCopy, onPreview }) => (
  <div className="flex items-center gap-2">
    <span className="text-xs font-medium text-slate-500 w-8">{label}</span>
    <div className="flex-1 bg-slate-50 rounded px-2 py-1 text-xs text-slate-600 truncate font-mono">
      {url}
    </div>
    <Button 
      variant="ghost" 
      size="sm" 
      className="h-7 w-7 p-0"
      onClick={() => onCopy(url, id)}
    >
      {copiedUrl === id ? (
        <Check className="w-3 h-3 text-emerald-500" />
      ) : (
        <Copy className="w-3 h-3" />
      )}
    </Button>
    <Button 
      variant="ghost" 
      size="sm" 
      className="h-7 w-7 p-0"
      onClick={() => onPreview(url)}
    >
      <ExternalLink className="w-3 h-3" />
    </Button>
  </div>
);

export default PortalExportsPage;
